import { useEffect, useMemo, useState } from "react";
import { Skeleton, Typography } from "@/components/atoms";
import { DataTable, DataTableSorting, Pagination } from "@/components/molecules";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGetPreferredDriversQuery } from "@/redux/apis/clientDispatch/clientDispatchApi";
import { PreferredDriversEmptyState } from "./components/PreferredDriversEmptyState";
import { preferredDriversColumns } from "./fixtures";
import { generatePreferredDriversData, sortPreferredDrivers } from "./helpers";
import { PreferredDriversSortKey } from "./types";

export const PreferredDriversTable = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sorting, setSorting] = useState<DataTableSorting>([]);

  const { accountId } = useClientScope();

  const { data, isLoading } = useGetPreferredDriversQuery({ accountId });

  // Reset to the first page whenever the sort changes.
  useEffect(() => setPage(1), [sorting]);

  const sort = sorting[0];

  const sortedItems = useMemo(
    () => sortPreferredDrivers(data ?? [], sort?.id as PreferredDriversSortKey | undefined, !!sort?.desc),
    [data, sort]
  );

  const totalItems = sortedItems.length;
  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
  // Clamp so removing the last item of the last page doesn't strand us on an empty page.
  const currentPage = Math.min(page, pageCount);

  const tableData = useMemo(
    () => generatePreferredDriversData(sortedItems.slice((currentPage - 1) * pageSize, currentPage * pageSize), accountId),
    [sortedItems, currentPage, pageSize, accountId]
  );

  if (isLoading) return <Skeleton />;

  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-lg border border-neutral-mid-gray">
        <DataTable
          className="w-full overflow-x-auto !rounded-none !ring-0 [&_table]:table-auto [&_tr]:bg-white"
          data={tableData}
          columns={preferredDriversColumns}
          sorting={sorting}
          setSorting={setSorting}
          emptyState={<PreferredDriversEmptyState />}
        />

        {totalItems > 0 && (
          <div className="flex items-center border-t border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2">
            <Typography variant="small" className="whitespace-nowrap text-neutral-silver-gray">
              {rangeStart}–{rangeEnd} of {totalItems}
            </Typography>
          </div>
        )}
      </div>

      {totalItems > 0 && (
        <Pagination
          className="!mt-0"
          currentPage={currentPage}
          pageCount={pageCount}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageClick={setPage}
          onSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      )}
    </div>
  );
};
